import React, { FC, useState } from "react";
import {
  useStripe,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
} from "@stripe/react-stripe-js";
import {
  Box,
  Checkbox,
  createStandaloneToast,
  FormControl,
  FormLabel,
  Stack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { Button } from "./Button";
import { ReactComponent as PaymentLockIcon } from "../assets/paymentLock.svg";
import { ReactComponent as PaymentModesIcon } from "../assets/paymentModes.svg";
import { LegalText } from "./LegalText";
import { Platform, SUCCESS_TOAST_DURATION } from "../common";

const toast = createStandaloneToast();
export const StripeCardAdd: FC<{
  onSubmit: (paymentId: string) => void;
  loading: boolean;
  buttonText: string;
  stepNo: 1 | 2;
  requiresLegal?: boolean;
  platform?: Platform;
}> = (props) => {
  const {
    onSubmit,
    loading,
    buttonText,
    stepNo,
    requiresLegal = false,
    platform,
  } = props;
  const [isLargerThan600] = useMediaQuery("(min-width: 600px)");
  const [fetchingPaymentId, setFetchingPaymentId] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [legalStatus, setLegalStatus] = useState(false);
  const handleSubmit = async (event: React.ChangeEvent<HTMLDivElement>) => {
    // Block native form submission.
    event.preventDefault();

    // Checking legal checkbox is mandatory
    // only if the prop says so ::tongue::
    if (requiresLegal && !legalStatus) {
      toast({
        title: "Marque la casilla de verificación de términos y condiciones",
        position: "top",
        duration: SUCCESS_TOAST_DURATION,
        status: "warning",
      });

      return;
    }

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      return;
    }

    // Use your card Element with other Stripe.js APIs
    setFetchingPaymentId(true);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });
    if (error || !paymentMethod) {
      console.log("[error]", error);
    } else {
      console.log("[PaymentId]", paymentMethod.id);
      onSubmit(paymentMethod.id);
    }
    setFetchingPaymentId(false);
  };
  return (
    <Box
      as="form"
      // width="500px"
      onSubmit={handleSubmit}
      flexBasis="60%"
      padding="5"
    >
      <Stack spacing="8">
        {isLargerThan600 && (
          <Stack spacing="4">
            <Text textStyle="bodyHeavy">
              {stepNo === 2 ? "Paso 2: " : null}Añade los datos de tu targeta
            </Text>
            <PaymentModesIcon />
          </Stack>
        )}
        <FormControl>
          <FormLabel>
            <Stack direction="row">
              <Text as="span" color="#9FAABB" textStyle="smallBold">
                Numero de Targeta
              </Text>
              {!isLargerThan600 && <PaymentModesIcon />}
            </Stack>
          </FormLabel>
          <Box
            border="3px solid #DEE8F3"
            borderRadius="15px"
            paddingX="3"
            paddingY="2"
          >
            <CardNumberElement
              onReady={(el) => el.focus()}
              options={{
                style: {
                  base: {
                    fontSize: "18px",
                  },
                },
              }}
            />
          </Box>
        </FormControl>
        <Stack direction={"row"} spacing="2">
          <FormControl flexBasis="67%">
            <FormLabel>
              <Text as="span" color="#9FAABB" textStyle="smallBold">
                Fecha de expiración
              </Text>
            </FormLabel>
            <Box
              border="3px solid #DEE8F3"
              borderRadius="15px"
              paddingX="3"
              paddingY="2"
            >
              <CardExpiryElement
                options={{
                  style: {
                    base: {
                      fontSize: "18px",
                    },
                  },
                }}
              />
            </Box>
          </FormControl>

          <FormControl flexBasis="33%">
            <FormLabel>
              <Text as="span" color="#9FAABB" textStyle="smallBold">
                CVV
              </Text>
            </FormLabel>
            <Box
              border="3px solid #DEE8F3"
              borderRadius="15px"
              paddingX="3"
              paddingY="2"
            >
              <CardCvcElement
                options={{
                  style: {
                    base: {
                      fontSize: "18px",
                    },
                  },
                }}
              />
            </Box>
          </FormControl>
        </Stack>
        {requiresLegal && platform ? (
          <FormControl isRequired>
            <Stack direction="row" align="flex-start" spacing="0">
              <Checkbox
                name="legalCheckbox"
                onChange={(e) => setLegalStatus(e.target.checked)}
              />
              <LegalText platform={platform} />
            </Stack>
          </FormControl>
        ) : null}
        <Stack direction="row" justify="flex-start">
          <Button
            isLoading={loading || fetchingPaymentId}
            variant="pulpo"
            type="submit"
            disabled={!stripe}
          >
            <Stack direction="row" spacing="1" align="center">
              <PaymentLockIcon />
              <Text textStyle="button">{buttonText}</Text>
            </Stack>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
